import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import ActionsMenu from './ActionsMenu';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';
import {
  PERIOD_LOCK_DEFINITION_CUSTOMERS,
  PERIOD_LOCK_DEFINITION_TABLE_KEY,
  PERIOD_LOCK_DEFINITION_URL,
} from '../constants';
import ConfirmDialog from '../../ConfirmDialog';
import { ICmdTableFilter } from '../../CmdTable/types';
import { PeriodLockDefinitionRecord } from '../interfaces';
import { putRequest } from '../../../Services/HttpService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { filterForRequestAtomFamily, sortFilterAtomFamily } from '../../CmdTable/CmdTableState';
import useAnalytics from '../../../hooks/useAnalytics';
import { getTableName } from '../../CmdTable/CmdTableService';

export interface IActionsProps<T extends PeriodLockDefinitionRecord> {
  data: T;
}

declare global {
  interface Window {
    resizeTimeout?: number;
  }
}

function Actions<T extends PeriodLockDefinitionRecord>(props: IActionsProps<T>): JSX.Element {
  const [pathname, navigate] = useLocation();
  const [isActionsMenuOpened, setIsActionsMenuOpened] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const { formatMessage: f } = useIntl();
  const { setDocumentTitle } = useDocumentTitle();
  const [isDiscardConfirmDialogOpen, setIsDiscardConfirmDialogOpen] = React.useState<boolean>();
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(PERIOD_LOCK_DEFINITION_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(PERIOD_LOCK_DEFINITION_TABLE_KEY)
  );
  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();
  const menuRef: React.RefObject<HTMLDivElement> = React.createRef();
  const lastPosition = React.useRef<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isMenuVisible, setIsMenuVisible] = React.useState(true);
  const selectedCustomers = useRecoilValue(selectedCustomersAtom);
  const { trackEvent } = useAnalytics();

  useOutsideClick(
    clickOutsideRef,
    (e) => {
      setIsActionsMenuOpened(false);
    },
    [menuRef]
  );

  useKeyPress(ESCAPE_KEY, () => {
    setIsActionsMenuOpened(false);
  });

  const isElementVisible = React.useCallback((): boolean => {
    const rect = clickOutsideRef.current?.getBoundingClientRect();
    const header = document.getElementById('cmd-table-header');
    const table = document.getElementById('cmd-table');

    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    const tableTop = table ? table.getBoundingClientRect().top : 0;
    const tableBottom = table ? table.getBoundingClientRect().bottom : 0;

    return !!(
      rect &&
      rect.top >= headerBottom && // Ensure the top of the element is below the bottom of the header
      rect.bottom <= tableBottom && // Ensure the bottom of the element is above the bottom of the table
      rect.right > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth)
    );
  }, [clickOutsideRef]);

  const updateMenuPosition = React.useCallback(() => {
    if (clickOutsideRef.current) {
      const rect = clickOutsideRef.current.getBoundingClientRect();
      const newPosition = {
        top: rect.bottom + 9 + window.scrollY,
        left: rect.right - 139 + window.scrollX,
      };

      // Only update if position actually changes
      if (
        newPosition.top !== lastPosition.current.top ||
        newPosition.left !== lastPosition.current.left
      ) {
        setMenuPosition(newPosition);
        lastPosition.current = newPosition; // Update the last known position
      }
    }
  }, [clickOutsideRef]);

  const handleResizeOrScroll = React.useCallback(() => {
    clearTimeout(window.resizeTimeout);
    window.resizeTimeout = window.setTimeout(() => {
      if (isActionsMenuOpened) {
        if (isElementVisible()) {
          updateMenuPosition();
          setIsMenuVisible(true);
        } else {
          setIsMenuVisible(false);
          // setIsActionsMenuOpened(false);
        }
      }
    }, 100);
  }, [isActionsMenuOpened, updateMenuPosition, isElementVisible]);

  React.useEffect(() => {
    window.addEventListener('scroll', handleResizeOrScroll);
    window.addEventListener('resize', handleResizeOrScroll);

    // Initial position update
    if (isActionsMenuOpened) {
      updateMenuPosition();
      setIsMenuVisible(isElementVisible());
    }

    return () => {
      window.removeEventListener('scroll', handleResizeOrScroll);
      window.removeEventListener('resize', handleResizeOrScroll);
      clearTimeout(window.resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleResizeOrScroll, isActionsMenuOpened, updateMenuPosition]);

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [PERIOD_LOCK_DEFINITION_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
    queryClient.invalidateQueries({
      queryKey: [PERIOD_LOCK_DEFINITION_CUSTOMERS, selectedCustomers],
    });
  };

  const handleActions = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_TABLE_KEY)}`, 'click', 'Open actions']);
    const newState = !isActionsMenuOpened;
    setIsActionsMenuOpened(newState);
    if (newState) {
      const isVisible = isElementVisible();
      setIsMenuVisible(isVisible);
      if (isVisible) {
        updateMenuPosition();
      }
    }
  };

  const handleDiscardConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_TABLE_KEY)}`, 'click', 'Discard action']);

    setIsActionsMenuOpened(false);
    setIsDiscardConfirmDialogOpen(true);
  };

  const handleCancelDiscard = () => {
    setIsDiscardConfirmDialogOpen(false);
    trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_TABLE_KEY)}`, 'click', 'Cancel discard']);
  };

  const handleSubmitConfirmDialog = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_TABLE_KEY)}`, 'click', 'Submit discard confirm dialog']);

    setIsActionsMenuOpened(false);
    const ids = { [props.data.customer.id]: [props.data.id] };
    const response = await putRequest(
      `${PERIOD_LOCK_DEFINITION_TABLE_KEY}/${props.data.id}/remove`,
      { ids },
      {
        returnAxiosResponse: true,
      }
    );
    if (response) {
      updateTable();
    }
    setIsDiscardConfirmDialogOpen(false);
  };

  const handleOpenModal = () => {
    trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_TABLE_KEY)}`, 'click', 'Open detail modal']);
    navigate(
      `${PERIOD_LOCK_DEFINITION_URL}/edit/${props.data.id}?customerId=${props.data.customer.id}`
    );
  };

  const handleCloseMenu = () => {
    trackEvent([`table ${getTableName(PERIOD_LOCK_DEFINITION_TABLE_KEY)}`, 'click', 'Close actions']);
    setIsActionsMenuOpened(false);
  };

  return (
    <div
      ref={clickOutsideRef}
      style={{
        zIndex: isActionsMenuOpened ? 10 : 4,
        margin: 'auto',
      }}
      key={props.data.id}
    >
      <div className="">
        <a
          href="#"
          className="text-gray-lighten"
          onClick={handleActions}
          style={{ padding: '15px', margin: '-15px' }}
          id={`rideActions-${props.data.id}`}
          data-cy="actions-menu-ride"
        >
          <i className="cmd-icons cmd-icon-more-horizontal fs-4" id={`ride-${props.data.id}`} />
        </a>
        {isActionsMenuOpened && isMenuVisible && (
          <ActionsMenu
            data={props.data}
            showDetailModalCallback={handleOpenModal}
            showDiscardConfirmModalCallback={handleDiscardConfirmDialog}
            closeCallback={handleCloseMenu}
            position={menuPosition}
            ref={menuRef}
            updateTable={updateTable}
          />
        )}
      </div>
      {isDiscardConfirmDialogOpen && (
        <ConfirmDialog
          title={f({
            id: 'table.confirm.dialog.remove.title',
          })}
          cancelCallback={handleCancelDiscard}
          continueCallback={handleSubmitConfirmDialog}
          cancelButtonTitle={f({ id: 'form.cancel' })}
          continueButtonTitle={f({ id: 'table.deleteBtn' })}
        >
          {f({ id: 'table.confirm.dialog.remove.message' })}
        </ConfirmDialog>
      )}
    </div>
  );
}

export default Actions;
